import React from 'react';
import { useQuery } from 'react-query';

import { getCommunityReviews } from 'apis/ReviewsAPI';
import { ReviewsTable, toReviewRows } from 'components/Alerts/AlertsReviewsTable';
import { REVIEW_SOURCE } from 'components/GoogleMetricsReviews/reviews/constants';
import ErrorMessage from 'components/lib/ErrorMessage';
import PillSwitcher from 'components/lib/PillSwitcher';

import MyCommunitiesDetailsModal from './MyCommunitiesDetailsModal';

type CommunityReview = {
  id: number;
  name: string | null;
  type: number;
  rating: number | null;
  score: number | null;
  categories: { id: number; label: string; score: number | null }[];
  date: string;
  text: string | null;
};

const UNKNOWN_REVIEW_SOURCE = 'Unknown';

type ReviewsTab = 'all' | 'negative' | 'positive';

const REVIEWS_TABS: { key: ReviewsTab; label: string; matches: (review: CommunityReview) => boolean }[] = [
  { key: 'all', label: 'All', matches: () => true },
  { key: 'negative', label: 'Negative', matches: (review) => (review.score ?? 0) < 0 },
  { key: 'positive', label: 'Positive', matches: (review) => (review.score ?? 0) > 0 },
];

const toReviewSourceRows = (reviews: CommunityReview[]) =>
  reviews.map((review) => ({
    id: review.id,
    from_name: review.name || '',
    from_source: REVIEW_SOURCE[review.type as keyof typeof REVIEW_SOURCE] || UNKNOWN_REVIEW_SOURCE,
    rating: review.rating,
    score: review.score,
    categories: review.categories,
    date: review.date,
    details: review.text || '',
  }));

type Props = {
  communityId: number;
  communityName: string;
  timePeriodLabel: string;
  timePeriodDays: number;
  onClose: () => void;
};

const ReviewsModal = ({ communityId, communityName, timePeriodLabel, timePeriodDays, onClose }: Props) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['my-communities-reviews', communityId, timePeriodDays],
    queryFn: () => getCommunityReviews(communityId, { days: timePeriodDays }),
    enabled: Boolean(communityId),
    refetchOnWindowFocus: false,
  });

  const [activeTab, setActiveTab] = React.useState<ReviewsTab>('all');

  const reviews: CommunityReview[] = data?.results ?? [];
  const tabs = REVIEWS_TABS.map((tab) => ({
    key: tab.key,
    label: tab.label,
    count: reviews.filter(tab.matches).length,
  }));

  const rows = React.useMemo(() => {
    const matches = REVIEWS_TABS.find((tab) => tab.key === activeTab)!.matches;
    return toReviewRows(toReviewSourceRows((data?.results ?? []).filter(matches)));
  }, [data, activeTab]);

  return (
    <MyCommunitiesDetailsModal
      open
      onClose={onClose}
      title={communityName}
      subtitle={`Reviews received in the ${timePeriodLabel.toLowerCase()}`}
    >
      <ErrorMessage text="Failed to load reviews. Please try again." active={isError} />
      <PillSwitcher items={tabs} activeKey={activeTab} onChange={setActiveTab} />
      <ReviewsTable rows={rows} loading={isLoading} />
    </MyCommunitiesDetailsModal>
  );
};

export default ReviewsModal;
